import React from 'react';
import {
  Box,
  Typography,
  Dialog,
  IconButton,
  Grid,
  Paper,
  Divider,
  Fade,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import { copyToClipboard, resolveCssVarValue } from '../../utils/CommonFunction';
import Utils from '../../utils';
import CustomSpinner from '../../common/CustomSpinner';
import { ReactComponent as FileCopy } from '../../assets/image/svg/fileCopy.svg';
import CheckIcon from '@mui/icons-material/Check';
import CarIcon from '@mui/icons-material/DirectionsCar';
import NHTSALOGO from '../../assets/image/png/nhtsa-brand.png';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CustomTooltip from '../../common/CustomTooltip';
// ====================== VIN DETAILS MODAL ======================

const VinDetailsModal = ({ open, onClose, vin, details, loading, error }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopyVin = () => {
    if (!vin?.value) return;
    copyToClipboard(vin?.value);
    setCopied(true);
    Utils.showAlert(1, 'VIN Copied successfully');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          borderRadius: 'calc(var(--sp-md2)*1.509434) !important',
          overflow: 'hidden',
          maxWidth: 'calc(var(--fs-hero)*19.867924528)',
          background: 'linear-gradient(145deg, #ffffff, #f8f9fc)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            boxShadow: '0 30px 60px rgba(0,0,0,0.2)'
          }
        }
      }}>
      {/* Premium Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          px: 'calc(var(--sp-lg)*1.32075)',
          py: 'calc(var(--sp-md2)*1.50943)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #eee'
        }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={'calc(var(--sp-md2) * 1.509434)'}>
          <CarIcon sx={{ fontSize: 'calc(var(--fs-xl)*1.32075)', color: 'white' }} />
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: 'calc(var(--fs-xs)*1.88679)' }}>
            Vehicle Information
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: 'white', '&:hover': { background: 'rgba(255,255,255,0.15)' } }}>
          <CloseIcon sx={{ fontSize: 'calc(var(--fs-xl)*1.132075)' }} />
        </IconButton>
      </Box>

      <Box sx={{ p: 'calc(var(--sp-lg)*1.132075)' }}>
        {/* VIN Display */}
        <Paper
          elevation={2}
          sx={{
            p: 'var(--sp-lg)',
            mb: 'calc(var(--sp-lg)*1.32075)',
            background: 'linear-gradient(145deg, #f0f4ff, #e6f0ff)',
            border: '1px solid rgba(22,93,255,0.15)',
            borderRadius: 'calc(var(--sp-md2)*1.50943)',
            transition: 'transform 0.2s ease',
            '&:hover': {
              transform: 'translateY(-2px)'
            }
          }}>
          <Typography
            variant="overline"
            fontWeight={500}
            color="text.secondary"
            gutterBottom
            sx={{ fontSize: 'calc(var(--fs-xs)*1.32075)' }}>
            {vin?.label || 'VIN Number'}
          </Typography>
          <Box display="flex" alignItems="center" gap={'calc(var(--sp-md2)*1.50943)'}>
            <Typography
              variant="h6"
              fontFamily="monospace"
              fontWeight={700}
              sx={{
                letterSpacing: 'calc(var(--sp-md2)*0.094339)',
                flex: 1,
                fontSize: 'calc(var(--fs-xs)*1.886792)'
              }}>
              {vin?.value || '—'}
            </Typography>
            <IconButton
              onClick={handleCopyVin}
              size="small"
              color="primary"
              title="Copy VIN"
              sx={{
                background: copied ? 'rgba(22,93,255,0.1)' : 'transparent',
                color: copied ? '#165DFF' : 'text.secondary',
                '&:hover': { background: 'rgba(22,93,255,0.1)' }
              }}>
              {copied ? (
                <CheckIcon sx={{ fontSize: 'calc(var(--fs-xl)*1.132075)' }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 'calc(var(--fs-xl)*1.132075)' }} />
              )}
            </IconButton>
          </Box>
        </Paper>

        {/* Content */}
        {loading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={'calc(var(--fs-hero)*1.132075)'}>
            <CustomSpinner size={'calc(var(--fs-hero)*1.226415)'} />
            <Typography
              mt={'calc(var(--sp-lg)*1.132075)'}
              color="text.secondary"
              fontWeight={600}
              sx={{ fontSize: 'calc(var(--fs-xs)*1.50943)' }}>
              Getting vehicle details...
            </Typography>
          </Box>
        ) : error ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={'calc(var(--sp-md2)*1.50943)'}
            gap={'calc(var(--sp-md2)*1.32075)'}
            sx={{
              color: '#dc2626',
              textAlign: 'center'
            }}>
            {/* Error Icon */}
            <ErrorOutlineIcon
              sx={{
                fontSize: 'calc(var(--fs-xl)*1.50943)',
                color: '#dc2626'
              }}
            />
            <Typography
              color="error"
              textAlign="center"
              fontWeight={500}
              sx={{ fontSize: 'calc(var(--fs-xs)*1.50943)' }}>
              {error}
            </Typography>
          </Box>
        ) : details ? (
          <Grid container spacing={resolveCssVarValue('--sp-xxs') * 0.4716981132}>
            {[
              { label: 'Make', value: details.Make },
              { label: 'Model', value: details.Model },
              { label: 'Year', value: details.ModelYear },
              { label: 'Vehicle Type', value: details.VehicleType },
              { label: 'Body Style', value: details.BodyClass },
              { label: 'Engine', value: details.EngineModel || details.EngineConfiguration },
              { label: 'Drive Type', value: details.DriveType },
              { label: 'Fuel Type', value: details.FuelTypePrimary }
            ].map((item, index) => (
              <Grid item xs={6} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 'calc(var(--sp-md2)*1)',
                    borderRadius: 'calc(var(--sp-md2)*1.132075)',
                    background: 'rgba(22, 104, 255, 0.03)',
                    border: '1px solid rgba(22, 92, 255, 0.13)',
                    transition: 'transform 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(22,93,255,0.1)'
                    }
                  }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={500}
                    sx={{ fontSize: 'calc(var(--fs-xs)*1.32075)' }}>
                    {item.label}
                  </Typography>
                  <Typography
                    variant="body1"
                    title={item.value || 'N/A'}
                    fontWeight={600}
                    sx={{
                      lineHeight: resolveCssVarValue('--fs-xs') * 0.1226415,
                      fontSize: 'calc(var(--fs-xs)*1.50943)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      '&[title]:hover::after': {
                        // content: 'attr(title)',
                      }
                    }}>
                    {item.value || 'N/A'}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : null}
      </Box>

      <Divider sx={{ borderColor: 'rgba(22,93,255,0.1)' }} />
      <Box
        sx={{
          p: 'calc(var(--sp-lg)*1.132075)',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'calc(var(--sp-md2)*1.132075)'
        }}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={500}
          sx={{ fontSize: 'calc(var(--fs-xs)*1.32075)' }}>
          Data provided by
        </Typography>
        <img src={NHTSALOGO} alt="NHTSA Logo" style={{ opacity: 0.8, width: '12%' }} />
      </Box>
    </Dialog>
  );
};

// ====================== MAIN COMPONENT ======================

const InfoCard = (props) => {
  const [copiedKey, setCopiedKey] = React.useState(null);
  const timerRef = React.useRef(null);

  const [openVinModal, setOpenVinModal] = React.useState(false);
  const [currentVin, setCurrentVin] = React.useState({ value: '', label: '' });
  const [vinDetails, setVinDetails] = React.useState(null);
  const [loadingVin, setLoadingVin] = React.useState(false);
  const [error, setError] = React.useState(null);

  const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/;

  // Group info in pairs (kept your logic but optimized)
  const groupedInfo = React.useMemo(() => {
    const result = [];
    for (let i = 0; i < props.info.length; i += 2) {
      result.push(props.info.slice(i, i + 2).reverse());
    }
    return result;
  }, [props.info]);

  const handleCopy = (key, value, isVin = false) => {
    if (!value) return;
    try {
      copyToClipboard(value);
      Utils.showAlert(1, isVin ? 'VIN Copied successfully' : 'Order Name Copied successfully');
    } catch (e) {
      Utils.showAlert(2, 'Failed to copy');
    } finally {
      setCopiedKey(key);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopiedKey(null), 1200);
    }
  };

  const handleVinInfo = async (vin, label) => {
    setCurrentVin({ value: vin, label });
    setOpenVinModal(true);
    setLoadingVin(true);
    setError(null);
    setVinDetails(null);

    if (!VIN_REGEX.test(vin)) {
      setError('Invalid VIN format');
      setLoadingVin(false);
      return;
    }

    const data = await decodeVin(vin);

    const hasError = data?.ErrorCode?.split(',').some((code) => code !== '0');
    if (hasError || !data) {
      setError(data?.ErrorText || 'Failed to decode VIN');
      setVinDetails(null);
    } else {
      setVinDetails(data);
    }

    setLoadingVin(false);
  };

  const handleClose = () => {
    setOpenVinModal(false);
    // Optional: reset after close
    setTimeout(() => {
      setVinDetails(null);
      setError(null);
    }, 300);
  };

  async function decodeVin(vin) {
    try {
      const res = await fetch(
        `https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValuesExtended/${vin}?format=json`
      );
      const json = await res.json();
      return json?.Results?.[0] || null;
    } catch (e) {
      console.error('VIN decode failed', e);
      return null;
    }
  }

  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <Box style={props.style} className="order-details">
      {groupedInfo.map((row, rowIndex) => (
        <Box key={rowIndex} className="info-row">
          {row.map((i) => (
            <Box key={i.label} className="info-item">
              {/* ===== LABEL SECTION ===== */}
              <Box
                className="label"
                sx={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)' }}>
                <Typography className="label">{i.label}</Typography>

                {i?.tooltipText && (
                  <CustomTooltip title={i.tooltipText} arrow placement="top">
                    <InfoIcon sx={{ fontSize: 'calc(var(--fs-xl) * 1)' }} />
                  </CustomTooltip>
                )}

                {i?.isVinCopy && i.value && (
                  <IconButton
                    onClick={() => handleVinInfo(i.value, i.label)}
                    sx={{ padding: 0, margin: 0 }}>
                    <CustomTooltip title="View Vehicle Details" arrow placement="top"><InfoIcon sx={{ fontSize: 'calc(var(--fs-xl) * 1)' }} /></CustomTooltip>
                  </IconButton>
                )}

                {i?.copyLabel && i?.copyPlacement === 'label' && (i?.copyValue || i?.value) && (
                  <span
                    className={`copy-icon ${copiedKey === i.label ? 'copied' : ''}`}
                    onClick={() => handleCopy(i.label, i?.copyValue || i?.value, !!i?.isVinCopy)}
                    title={copiedKey === i.label ? 'Copied!' : 'Copy'}
                    role="button"
                    aria-label="Copy Order Name value"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCopy(i.label, i?.copyValue || i?.value, !!i?.isVinCopy);
                      }
                    }}>
                    <FileCopy width="var(--fs-lg)" height="var(--fs-lg)" />
                  </span>
                )}
              </Box>

              {/* ===== VALUE SECTION ===== */}
              <Box
                className="value-stack"
                sx={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)' }}>
                <Typography
                  className={`value-text ${/address/i.test(i.label) ? 'wrap' : ''}`}
                  title={i.value}
                  sx={
                    i.isVinCopy ? { cursor: 'pointer', '&:hover': { color: 'primary.main' } } : {}
                  }
                  onClick={i.isVinCopy ? () => handleVinInfo(i.value) : undefined}>
                  {i.value || i.defaultValue || '—'}
                </Typography>

                {i?.copyLabel && i?.copyPlacement !== 'label' && (i?.copyValue || i?.value) && (
                  <span
                    className={`copy-icon ${copiedKey === i.label ? 'copied' : ''}`}
                    onClick={() => handleCopy(i.label, i?.copyValue || i?.value, !!i?.isVinCopy)}
                    title={copiedKey === i.label ? 'Copied!' : 'Copy'}
                    role="button"
                    aria-label="Copy value"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCopy(i.label, i?.copyValue || i?.value, !!i?.isVinCopy);
                      }
                    }}>
                    <FileCopy width="var(--fs-lg)" height="var(--fs-lg)" />
                  </span>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      ))}
      <VinDetailsModal
        open={openVinModal}
        onClose={handleClose}
        vin={currentVin}
        details={vinDetails}
        loading={loadingVin}
        error={error}
      />
    </Box>
  );
};

export default InfoCard;
